import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Button,
  Typography,
  Tag,
  Row,
  Col,
  Input,
  message,
  Spin,
  Skeleton,
  Modal,
  Alert,
  Tooltip,
  Tour,
} from 'antd';
import {
  CheckOutlined,
  DatabaseOutlined,
  ExclamationCircleOutlined,
  FireOutlined,
  CopyOutlined,
  ImportOutlined,
} from '@ant-design/icons';
import { v2ArticlesApi, creditsApi, publicSamplesApi } from '../../../../api';
import { getUserProfile } from '../../../../auth';
import TipsRotator from '../../../Common/TipsRotator';
import { generationStep4_1Tips, waitingTips } from '../../../Common/tipsData';
import { calculateActualUsageCredits } from '../../../../utils/creditCalculation';
import { useNavigate } from 'react-router-dom';
import { useTutorialTour } from '../../../../hooks/useTutorialTour';
import { TOUR_CONFIGS } from '../../../../config/tourConfigs';
import EnhancedTour from '../../../Common/EnhancedTour';

const { Text } = Typography;
const { TextArea } = Input;

/**
 * STEP 4.1: 執筆プラン
 * 左7:3右カラムレイアウト、STEP3の構成案を表示
 */
const Step4_1_WritingPlan = ({
  articleUuid,
  articleData,
  writingPlan,
  onCompleted,
  isReadOnly = false,
  isGuest = false,
}) => {
  const navigate = useNavigate();

  // ツアー管理
  const {
    tourOpen,
    completeTour,
    skipTour,
    restartTour,
    currentStep,
    updateCurrentStep,
    loading: tourLoading,
    isMicroMode,
  } = useTutorialTour('generation_step4_1', { microKey: 'generation_step4_1_micro', disabled: isGuest });
  const tourStartedRef = useRef(false);

  // 親コンポーネントからのツアー再表示イベントをリッスン
  useEffect(() => {
    const handleRestartTour = () => restartTour();
    window.addEventListener('restartGenerationTour', handleRestartTour);
    return () => window.removeEventListener('restartGenerationTour', handleRestartTour);
  }, [restartTour]);

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false); // 執筆プラン生成中
  const [generationStarted, setGenerationStarted] = useState(false); // 生成開始フラグ
  const [sections, setSections] = useState([]); // STEP3の構成案（フラット化）
  const [workingDraftSections, setWorkingDraftSections] = useState([]); // STEP3の構成案（階層構造保持）
  const [selectedSection, setSelectedSection] = useState(null);
  const [h1Title, setH1Title] = useState(''); // H1タイトル
  const [writingPlansData, setWritingPlansData] = useState(null); // 生成された執筆プラン
  const [formData, setFormData] = useState({
    section_goal: '',
    outline_points: ['', '', ''],
    remarks: '',
  });
  const [saving, setSaving] = useState(false); // 一時保存中
  const [userRole, setUserRole] = useState('user'); // ユーザー権限
  const [userPlanType, setUserPlanType] = useState('free'); // ユーザーのプランタイプ
  const [importModalVisible, setImportModalVisible] = useState(false); // インポートモーダル
  const [importJson, setImportJson] = useState(''); // インポートするJSON
  const [importError, setImportError] = useState(''); // インポートエラー
  const previousSectionRef = useRef(null); // 前回選択されていたセクション
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // 未保存の変更があるか

  // 🔥 クレジット関連のステート
  const [usageCredits, setUsageCredits] = useState(0);
  const [creditBreakdown, setCreditBreakdown] = useState([]);
  const [availableCredits, setAvailableCredits] = useState(0);
  const [loadingCredits, setLoadingCredits] = useState(true);
  // 執筆プラン生成完了時にツアーを開始
  useEffect(() => {
    if (
      !tourLoading &&
      !tourStartedRef.current &&
      writingPlansData &&
      writingPlansData.plans &&
      writingPlansData.plans.length > 0 &&
      selectedSection
    ) {
      tourStartedRef.current = true;
      // 少し遅延させてDOMの準備を待つ
      setTimeout(() => {
        // ツアーは useTutorialTour フックが自動的に開始する
      }, 500);
    }
  }, [tourLoading, writingPlansData, selectedSection]);

  useEffect(() => {
    if (isGuest) return;
    const loadUserProfile = async () => {
      try {
        const profile = await getUserProfile();
        setUserRole(profile?.role || 'user');
        setUserPlanType(profile?.plan?.type || 'free');
      } catch (error) {
        console.error('[Step4.1] Failed to get user profile:', error);
      }
    };
    loadUserProfile();
  }, [isGuest]);

  // STEP3の構成案を読み込み
  useEffect(() => {
    const loadOutline = async () => {
      if (!articleUuid) return;

      try {
        setLoading(true);
        const articleApi = isGuest ? publicSamplesApi : v2ArticlesApi;
        const article = await articleApi.getArticle(articleUuid);

        // working_draftから構成案を取得（正しいパス）
        const workingDraftData = article?.outline?.outline_data?.working_draft;
        const workingDraft = workingDraftData?.sections;

        // H1タイトルを取得
        const h1 = workingDraftData?.title || article?.title || '';
        setH1Title(h1);

        // 階層構造をそのまま保存
        if (workingDraft && Array.isArray(workingDraft)) {
          setWorkingDraftSections(workingDraft);
        }

        if (workingDraft && Array.isArray(workingDraft)) {
          // sectionsを展開（_child系は親のchildrenとして格納）
          const flatSections = [];
          let lastH2 = null;
          let lastH3 = null;
          let lastH4 = null;

          workingDraft.forEach(section => {
            // 展開モード（isExpansionSection）の場合はtoppingとして扱う
            if (section.isExpansionSection && section.expansionTopping) {
              const toppingItem = {
                id: section.id,
                text: section.title,
                type: 'topping',
                locked: section.locked,
                memo: section.memo,
                placementLevel: 'h2',
                toppingData: section.expansionTopping,
                children: [],
              };
              flatSections.push(toppingItem);
              lastH2 = null;
              lastH3 = null;
              lastH4 = null;
              return; // 次のsectionへ
            }

            // 通常のH2を追加
            const h2Item = {
              id: section.id,
              text: section.title,
              type: 'h2',
              locked: section.locked,
              memo: section.memo,
              placementLevel: null,
              children: [],
            };
            flatSections.push(h2Item);
            lastH2 = h2Item;
            lastH3 = null;
            lastH4 = null;

            // subsectionsがある場合は追加（2階層対応）
            if (section.subsections && Array.isArray(section.subsections)) {
              section.subsections.forEach(sub => {
                // H3 (type='heading')を追加
                if (sub.type === 'heading') {
                  const h3Item = {
                    id: sub.id,
                    text: sub.title,
                    type: 'h3',
                    locked: sub.locked,
                    memo: sub.memo,
                    placementLevel: null,
                    children: [],
                  };
                  flatSections.push(h3Item);
                  lastH3 = h3Item;
                  lastH4 = null;

                  // H3の中のsubsectionsも処理
                  if (sub.subsections && Array.isArray(sub.subsections)) {
                    sub.subsections.forEach(subSub => {
                      // H4を追加（親H3のchildrenに格納し、独立要素としては追加しない）
                      if (subSub.type === 'h4') {
                        const h4Item = {
                          id: subSub.id,
                          text: subSub.title,
                          type: 'h4',
                          locked: subSub.locked,
                          memo: subSub.memo,
                          placementLevel: null,
                          children: [],
                        };
                        // H4は親H3のchildrenに追加（独立要素としない）
                        if (lastH3) {
                          lastH3.children.push(h4Item);
                        }
                        lastH4 = h4Item;
                      }
                      // toppingを追加
                      else if (subSub.type === 'topping') {
                        const level = subSub.placementLevel || 'h3_child';
                        const toppingItem = {
                          id: subSub.id,
                          text:
                            subSub.title ||
                            subSub.toppingData?.title ||
                            '独自情報',
                          type: 'topping',
                          locked: subSub.locked,
                          memo: subSub.memo,
                          toppingData: subSub.toppingData,
                          placementLevel: level,
                          placementMode: subSub.placementMode, // 追加
                          content:
                            subSub.toppingData?.content || subSub.content, // 追加
                          children: [],
                        };

                        // _child系は親のchildrenに追加
                        if (
                          level === 'h3_child' ||
                          level === 'h4_child' ||
                          level === 'h2_child'
                        ) {
                          // _child系の処理
                          if (level === 'h3_child' && lastH3) {
                            lastH3.children.push(toppingItem);
                          } else if (level === 'h4_child') {
                            if (lastH4) {
                              lastH4.children.push(toppingItem);
                            } else if (lastH3) {
                              // H4がない場合はH3に追加
                              lastH3.children.push(toppingItem);
                            } else if (lastH2) {
                              lastH2.children.push(toppingItem);
                            }
                          } else if (level === 'h2_child' && lastH2) {
                            lastH2.children.push(toppingItem);
                          }
                        }
                        // h2, h3, h4（セクションそのもの）は独立アイテム
                        else if (
                          level === 'h2' ||
                          level === 'h3' ||
                          level === 'h4'
                        ) {
                          flatSections.push(toppingItem);
                        }
                      }
                    });
                  }
                }
                // H4を追加（H2直下のH4 - 本来はありえないが、もしあれば直前のH3に追加）
                else if (sub.type === 'h4') {
                  const h4Item = {
                    id: sub.id,
                    text: sub.title,
                    type: 'h4',
                    locked: sub.locked,
                    memo: sub.memo,
                    placementLevel: null,
                    children: [],
                  };
                  // 直前のH3があればそのchildrenに追加、なければ独立要素として追加
                  if (lastH3) {
                    lastH3.children.push(h4Item);
                  } else {
                    flatSections.push(h4Item);
                  }
                  lastH4 = h4Item;
                }
                // toppingを追加（H2直下のtopping）
                else if (sub.type === 'topping') {
                  const level = sub.placementLevel || 'h3_child';
                  const toppingItem = {
                    id: sub.id,
                    text: sub.title || sub.toppingData?.title || '独自情報',
                    type: 'topping',
                    locked: sub.locked,
                    memo: sub.memo,
                    toppingData: sub.toppingData,
                    placementLevel: level,
                    placementMode: sub.placementMode, // 追加
                    content: sub.toppingData?.content || sub.content, // 追加
                    children: [],
                  };

                  // _child系は親のchildrenに追加
                  if (
                    level === 'h3_child' ||
                    level === 'h4_child' ||
                    level === 'h2_child'
                  ) {
                    // _child系の処理
                    if (level === 'h3_child' && lastH3) {
                      lastH3.children.push(toppingItem);
                    } else if (level === 'h4_child') {
                      if (lastH4) {
                        lastH4.children.push(toppingItem);
                      } else if (lastH3) {
                        // H4がない場合はH3に追加
                        lastH3.children.push(toppingItem);
                      } else if (lastH2) {
                        lastH2.children.push(toppingItem);
                      }
                    } else if (level === 'h2_child' && lastH2) {
                      lastH2.children.push(toppingItem);
                    }
                  }
                  // h2, h3, h4（セクションそのもの）は独立アイテム
                  else if (level === 'h2' || level === 'h3' || level === 'h4') {
                    flatSections.push(toppingItem);
                  }
                }
              });
            }
          });

          setSections(flatSections);

          // 最初のH2を選択（formDataはwritingPlansDataのuseEffectで設定される）
          const firstH2 = flatSections.find(s => s.type === 'h2');
          if (firstH2) {
            setSelectedSection(firstH2);
            // ⭐ ダミーデータは設定しない
            // writingPlansDataが設定されると、useEffectで実際のデータが設定される
            // writingPlansDataがない場合は、autoGenerateWritingPlanで生成される
          }
        } else {
          message.warning(
            '構成案が見つかりません。STEP3で構成を作成してください。',
          );
        }
      } catch (error) {
        console.error('[Step4.1] Failed to load outline:', error);
        message.error('構成案の読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };

    loadOutline();
  }, [articleUuid]);

  // 🔥 クレジット計算（構成案が確定したら実行）
  useEffect(() => {
    const calculateAndFetchCredits = async () => {
      if (!workingDraftSections || workingDraftSections.length === 0) {
        setLoadingCredits(false);
        return;
      }

      try {
        setLoadingCredits(true);

        // 従量クレジット計算
        const { usageCredits: calculatedCredits, breakdown } =
          calculateActualUsageCredits(workingDraftSections);

        setUsageCredits(calculatedCredits);
        setCreditBreakdown(breakdown);

        if (!isGuest) {
          const creditInfo = await creditsApi.getMyCredits();
          setAvailableCredits(creditInfo.available || 0);
        }
      } catch (error) {
        console.error('❌ [STEP4.1] Failed to calculate/fetch credits:', error);
      } finally {
        setLoadingCredits(false);
      }
    };

    calculateAndFetchCredits();
  }, [workingDraftSections]);

  // 構成案読み込み完了後、自動的に執筆プラン生成を開始（閲覧専用時は既存データのみロード）
  useEffect(() => {
    let mounted = true; // マウント状態を追跡

    const autoGenerateWritingPlan = async () => {
      // マウント解除されていたらスキップ
      if (!mounted) return;

      // 生成開始済み、生成中、または構成案未読み込みの場合はスキップ
      if (generationStarted || generating || sections.length === 0) {
        return;
      }

      // 既に生成済みの場合はスキップ（ただし無効化されている場合は再生成）
      try {
        const wpApi = isGuest ? publicSamplesApi : v2ArticlesApi;
        const result = await wpApi.getWritingPlan(articleUuid);
        // 無効化フラグをチェック
        const isInvalidated =
          result.writing_plans_invalidated !== undefined &&
          result.writing_plans_invalidated !== null;

        if (
          result.status === 'completed' &&
          result.writing_plans &&
          !isInvalidated
        ) {
          if (!mounted) return; // アンマウントされていたらスキップ

          setWritingPlansData(result.writing_plans);
          setGenerationStarted(true);
          // useEffectで自動的に最初のH2が選択される
          return; // 既存データがあるのでここで終了
        }

        if (isInvalidated) {
          if (!mounted) return; // アンマウントされていたらスキップ

          // 無効化された場合、既存データをクリアしてスケルトン表示
          setWritingPlansData(null);
          setSelectedSection(null);
          setFormData({
            section_goal: '',
            outline_points: ['', '', ''],
            remarks: '',
          });
          // 無効化されている場合は再生成が必要なので、下に進む
        }
      } catch (error) {
        // not_generated の場合は新規生成を続行
        // 生成が必要なので、下に進む
      }

      if (!mounted) return; // アンマウントされていたらスキップ

      // ⭐ 閲覧専用モード/ゲスト時は新規生成をスキップ
      if (isReadOnly || isGuest) {
        return;
      }

      // 自動生成開始（既存データがない場合、または無効化されている場合のみ実行される）
      setGenerationStarted(true);
      await startGeneration();
    };

    if (articleUuid && sections.length > 0 && !loading) {
      autoGenerateWritingPlan();
    }

    // クリーンアップ関数
    return () => {
      mounted = false;
    };
  }, [
    articleUuid,
    sections,
    loading,
    generationStarted,
    generating,
    isReadOnly,
  ]);

  // writingPlansDataが設定されたら、選択中のセクションのformDataを更新
  useEffect(() => {
    if (writingPlansData && writingPlansData.plans && sections.length > 0) {
      // 選択中のセクションがあればそのデータを設定
      const targetSection = selectedSection || sections.find(s => s.type === 'h2');
      
      if (targetSection) {
        // selectedSectionがない場合は設定
        if (!selectedSection) {
          setSelectedSection(targetSection);
        }
        
        // h2_idでマッチング（h2_indexではなく）
        const plan = writingPlansData.plans?.find(p => p.h2_id === targetSection.id);
        if (plan && plan.h2_section) {
          setFormData({
            section_goal: plan.h2_section.section_goal || '',
            outline_points: plan.h2_section.outline_points || ['', '', ''],
            remarks: plan.h2_section.remarks || '',
          });
        }
      }
    }
  }, [writingPlansData, sections]);

  // 現在のセクションの編集内容をwritingPlansDataに反映（ローカルのみ、API呼び出しなし）
  const saveCurrentSectionToLocal = () => {
    if (!selectedSection || !writingPlansData) return writingPlansData;

    const updatedPlans = JSON.parse(JSON.stringify(writingPlansData));

    if (
      selectedSection.type === 'h2' ||
      (selectedSection.type === 'topping' &&
        selectedSection.placementLevel === 'h2')
    ) {
      // H2の場合
      const planIndex = updatedPlans.plans?.findIndex(
        p => p.h2_id === selectedSection.id,
      );
      if (planIndex !== -1 && updatedPlans.plans) {
        updatedPlans.plans[planIndex].h2_section = {
          ...updatedPlans.plans[planIndex].h2_section,
          section_goal: formData.section_goal,
          outline_points: formData.outline_points.filter(p => p.trim() !== ''),
          remarks: formData.remarks,
        };
      }
    } else {
      // H3/H4の場合
      const parentH2Data = workingDraftSections.find(h2Section => {
        if (h2Section.subsections) {
          const directChild = h2Section.subsections.some(
            sub => sub.id === selectedSection.id,
          );
          if (directChild) return true;
          for (const sub of h2Section.subsections) {
            if (sub.type === 'h3' && sub.subsections) {
              const h4Child = sub.subsections.some(
                h4 => h4.id === selectedSection.id,
              );
              if (h4Child) return true;
            }
          }
        }
        return false;
      });

      if (parentH2Data) {
        const planIndex = updatedPlans.plans?.findIndex(
          p => p.h2_id === parentH2Data.id,
        );
        if (planIndex !== -1 && updatedPlans.plans) {
          const h3Index = updatedPlans.plans[planIndex].h3_sections?.findIndex(
            h3 => h3.heading_id === selectedSection.id,
          );
          if (h3Index !== -1 && updatedPlans.plans[planIndex].h3_sections) {
            updatedPlans.plans[planIndex].h3_sections[h3Index] = {
              ...updatedPlans.plans[planIndex].h3_sections[h3Index],
              section_goal: formData.section_goal,
              outline_points: formData.outline_points.filter(
                p => p.trim() !== '',
              ),
              remarks: formData.remarks,
            };
          }
        }
      }
    }

    return updatedPlans;
  };

  // セクション選択時（変更前のセクションの編集内容を自動保存）
  const handleSelectSection = section => {
    // 前回選択していたセクションがあれば、編集内容をローカルに保存
    if (
      selectedSection &&
      writingPlansData &&
      selectedSection.id !== section.id
    ) {
      const updatedPlans = saveCurrentSectionToLocal();
      setWritingPlansData(updatedPlans);
    }

    setSelectedSection(section);

    // 生成された執筆プランから該当データを取得
    if (writingPlansData && writingPlansData.plans) {
      let planData = null;

      // H2の場合（通常のH2とH2独自情報）
      if (
        section.type === 'h2' ||
        (section.type === 'topping' && section.placementLevel === 'h2')
      ) {
        // IDベースでマッチング
        const plan = writingPlansData.plans.find(p => p.h2_id === section.id);
        if (plan && plan.h2_section) {
          planData = plan.h2_section;
        }
      }
      // H3, H4, H3独自情報の場合: 親H2を探してからIDでH3を探す
      else if (
        section.type === 'h3' ||
        section.type === 'h4' ||
        (section.type === 'topping' && section.placementLevel !== 'h2')
      ) {
        // workingDraftSections（階層構造保持）から親H2を探す
        const parentH2Data = workingDraftSections.find(
          h2Section =>
            h2Section.subsections &&
            h2Section.subsections.some(
              sub =>
                sub.id === section.id ||
                (sub.type === 'h3' &&
                  sub.subsections &&
                  sub.subsections.some(h4 => h4.id === section.id)),
            ),
        );
        // フラット化されたsectionsから同じH2を探す（IDベースでプラン取得用）
        if (parentH2Data) {
          // 親H2のIDでプランを探す
          const plan = writingPlansData.plans.find(
            p => p.h2_id === parentH2Data.id,
          );
          if (plan && plan.h3_sections) {
            // IDベースのマッチング（インデックスではなく、heading_idで照合）
            // 選択されたセクションのIDに一致するプランを探す
            planData = plan.h3_sections.find(
              h3Plan => h3Plan.heading_id === section.id,
            );

            // H4の場合、親H3のプランを使用する可能性も考慮
            if (!planData && section.type === 'h4' && parentH2Data) {
              // H4の親H3を探す
              for (const sub of parentH2Data.subsections || []) {
                if (
                  sub.subsections &&
                  sub.subsections.some(h4 => h4.id === section.id)
                ) {
                  // 親H3のIDでプランを探す
                  planData = plan.h3_sections.find(
                    h3Plan => h3Plan.heading_id === sub.id,
                  );
                  if (planData) {
                    break;
                  }
                }
              }
            }
          }
        }
      }

      // planDataがあればセット、なければダミーデータ
      if (planData) {
        setFormData({
          section_goal: planData.section_goal || '',
          outline_points: planData.outline_points || ['', '', ''],
          remarks: planData.remarks || '',
        });
      } else {
        // ダミーデータをセット
        setFormData({
          section_goal: `このセクションでは、読者に「${section.text}」について理解させることを目指します。`,
          outline_points: [
            `${section.text}の基本的な定義と背景を説明`,
            `具体的な実践方法やステップを詳しく解説`,
            `よくある間違いや注意点を事例とともに紹介`,
          ],
          remarks: `執筆時は、専門用語を噛み砕いて説明し、具体例を多く含めること。`,
        });
      }
    } else {
      // 執筆プラン未生成時はダミーデータ
      setFormData({
        section_goal: `このセクションでは、読者に「${section.text}」について理解させることを目指します。`,
        outline_points: [
          `${section.text}の基本的な定義と背景を説明`,
          `具体的な実践方法やステップを詳しく解説`,
          `よくある間違いや注意点を事例とともに紹介`,
        ],
        remarks: `執筆時は、専門用語を噛み砕いて説明し、具体例を多く含めること。`,
      });
    }
  };

  // フォーム変更
  const handleFormChange = (field, value, index = null) => {
    if (index !== null) {
      const newPoints = [...formData.outline_points];
      newPoints[index] = value;
      setFormData(prev => ({ ...prev, outline_points: newPoints }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    // 変更フラグを立てる
    setHasUnsavedChanges(true);
  };

  // 執筆プラン生成を開始
  const startGeneration = async () => {
    if (generating) return;

    try {
      setGenerating(true);
      message.loading({
        content: '執筆プランを生成中...',
        key: 'generating',
        duration: 0,
      });

      // 執筆プラン生成API呼び出し
      const response = await v2ArticlesApi.generateWritingPlan(articleUuid);
      
      // ⭐ 既に生成済みの場合、レスポンスに含まれるデータを使用
      if (response.status === 'completed' && response.writing_plans && response.writing_plans.plans) {
        message.destroy('generating');
        setWritingPlansData(response.writing_plans);
        setGenerating(false);
        setGenerationStarted(true);
        return; // ポーリング不要
      }
      
      // ポーリングで結果を取得（最大60秒、3秒間隔）
      let attempts = 0;
      const maxAttempts = 20;

      const pollResult = async () => {
        attempts++;
        const result = await v2ArticlesApi.getWritingPlan(articleUuid);
        if (
          result.status === 'completed' &&
          result.writing_plans &&
          result.writing_plans.plans &&
          !result.writing_plans_invalidated // 無効化されていない
        ) {
          message.destroy('generating');
          // サクセスメッセージは出さない（既存データの読み込み時）

          setWritingPlansData(result.writing_plans);
          setGenerating(false);
          // useEffectで自動的に最初のH2が選択される

          return true;
        } else if (result.status === 'completed') {
          console.warn(
            '[Step4.1] ⚠️ Status is completed but no plans data or invalidated:',
            result,
          );
        }

        if (attempts >= maxAttempts) {
          message.destroy('generating');
          message.warning(
            '執筆プランの生成に時間がかかっています。後ほど再度ご確認ください。',
          );
          setGenerating(false);
          return false;
        }

        // 3秒後に再試行
        await new Promise(resolve => setTimeout(resolve, 3000));
        return await pollResult();
      };

      await pollResult();
    } catch (error) {
      message.destroy('generating');
      message.error(
        error.response?.data?.detail || '執筆プランの生成に失敗しました',
      );
      setGenerating(false);
      console.error('[Step4.1] Failed to generate writing plan:', error);
    }
  };

  // 一時保存機能
  const handleSaveWritingPlan = async () => {
    if (!selectedSection || !writingPlansData) {
      message.warning('保存する執筆プランがありません');
      return;
    }

    try {
      setSaving(true);

      // 現在のformDataを取得
      const currentFormData = formData;

      // writingPlansDataを更新（ディープコピー）
      const updatedPlans = JSON.parse(JSON.stringify(writingPlansData));

      // 該当するプランを更新
      if (
        selectedSection.type === 'h2' ||
        (selectedSection.type === 'topping' &&
          selectedSection.placementLevel === 'h2')
      ) {
        // H2の場合
        const planIndex = updatedPlans.plans?.findIndex(
          p => p.h2_id === selectedSection.id,
        );
        if (planIndex !== -1 && updatedPlans.plans) {
          updatedPlans.plans[planIndex].h2_section = {
            ...updatedPlans.plans[planIndex].h2_section,
            section_goal: currentFormData.section_goal,
            outline_points: currentFormData.outline_points.filter(
              p => p.trim() !== '',
            ),
            remarks: currentFormData.remarks,
          };
        }
      } else {
        // H3/H4の場合: workingDraftSections（階層構造）から親H2を探す
        const parentH2Data = workingDraftSections.find(h2Section => {
          if (h2Section.subsections) {
            // H2の直接の子を確認
            const directChild = h2Section.subsections.some(
              sub => sub.id === selectedSection.id,
            );
            if (directChild) return true;

            // H3の子（H4）も確認
            for (const sub of h2Section.subsections) {
              if (sub.type === 'h3' && sub.subsections) {
                const h4Child = sub.subsections.some(
                  h4 => h4.id === selectedSection.id,
                );
                if (h4Child) return true;
              }
            }
          }
          return false;
        });

        if (parentH2Data) {
          const planIndex = updatedPlans.plans?.findIndex(
            p => p.h2_id === parentH2Data.id,
          );

          if (planIndex !== -1 && updatedPlans.plans) {
            const h3Index = updatedPlans.plans[
              planIndex
            ].h3_sections?.findIndex(
              h3 => h3.heading_id === selectedSection.id,
            );

            if (h3Index !== -1 && updatedPlans.plans[planIndex].h3_sections) {
              updatedPlans.plans[planIndex].h3_sections[h3Index] = {
                ...updatedPlans.plans[planIndex].h3_sections[h3Index],
                section_goal: currentFormData.section_goal,
                outline_points: currentFormData.outline_points.filter(
                  p => p.trim() !== '',
                ),
                remarks: currentFormData.remarks,
              };
            }
          }
        }
      }

      // バックエンドに保存
      const response = await v2ArticlesApi.saveWritingPlan(
        articleUuid,
        updatedPlans,
      );

      // レスポンスから更新されたデータを使用
      const savedPlans = response.writing_plans || updatedPlans;
      setWritingPlansData(savedPlans);
      setHasUnsavedChanges(false); // 保存完了で変更フラグをリセット

      message.success('執筆プランを保存しました');
      return true; // 保存成功
    } catch (error) {
      console.error('[Step4.1] Failed to save writing plan:', error);
      message.error('執筆プランの保存に失敗しました');
      return false; // 保存失敗
    } finally {
      setSaving(false);
    }
  };

  // 管理者用：執筆プランをJSONでコピー
  const handleCopyAsJson = () => {
    if (!writingPlansData) {
      message.warning('コピーする執筆プランがありません');
      return;
    }

    // 現在のセクションの編集内容も含める
    const updatedPlans = saveCurrentSectionToLocal();

    // 見出し情報を含めたエクスポート用データを作成
    const exportData = {
      plans: updatedPlans.plans?.map(plan => ({
        h2_id: plan.h2_id,
        h2_title: plan.h2_title,
        h2_type: plan.h2_type,
        h2_section: plan.h2_section,
        h3_sections: plan.h3_sections,
      })),
      generated_at: updatedPlans.generated_at,
    };

    try {
      navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
      message.success('執筆プランをクリップボードにコピーしました');
    } catch (error) {
      console.error('[Step4.1] Failed to copy:', error);
      message.error('コピーに失敗しました');
    }
  };

  // 管理者用：JSONインポートモーダルを開く
  const handleOpenImportModal = () => {
    setImportJson('');
    setImportError('');
    setImportModalVisible(true);
  };

  // 管理者用：JSONをインポート
  const handleImportJson = async () => {
    if (!importJson.trim()) {
      setImportError('JSONを入力してください');
      return;
    }

    try {
      const parsed = JSON.parse(importJson);

      // バリデーション
      if (!parsed.plans || !Array.isArray(parsed.plans)) {
        setImportError('無効なJSON形式です: plansが配列ではありません');
        return;
      }

      // 各プランの必須フィールドをチェック
      for (let i = 0; i < parsed.plans.length; i++) {
        const plan = parsed.plans[i];
        if (!plan.h2_id) {
          setImportError(`無効なJSON形式です: plans[${i}]にh2_idがありません`);
          return;
        }
        if (!plan.h2_section || typeof plan.h2_section !== 'object') {
          setImportError(
            `無効なJSON形式です: plans[${i}]にh2_sectionがありません`,
          );
          return;
        }
      }

      // 現在のwritingPlansDataとマージ
      const mergedPlans = {
        ...writingPlansData,
        plans: parsed.plans.map(importedPlan => {
          // 既存のプランを探す
          const existingPlan = writingPlansData?.plans?.find(
            p => p.h2_id === importedPlan.h2_id,
          );

          if (existingPlan) {
            // 既存のプランがあればマージ
            return {
              ...existingPlan,
              h2_section: importedPlan.h2_section || existingPlan.h2_section,
              h3_sections: importedPlan.h3_sections || existingPlan.h3_sections,
            };
          } else {
            // 新しいプランはそのまま追加
            return importedPlan;
          }
        }),
        generated_at: parsed.generated_at || writingPlansData?.generated_at,
      };

      // バックエンドに保存
      setSaving(true);
      const response = await v2ArticlesApi.saveWritingPlan(
        articleUuid,
        mergedPlans,
      );
      const savedPlans = response.writing_plans || mergedPlans;
      setWritingPlansData(savedPlans);

      // 選択中のセクションのフォームデータを更新
      if (selectedSection) {
        const currentPlan = savedPlans.plans?.find(
          p => p.h2_id === selectedSection.id,
        );
        if (currentPlan && currentPlan.h2_section) {
          setFormData({
            section_goal: currentPlan.h2_section.section_goal || '',
            outline_points: currentPlan.h2_section.outline_points || [
              '',
              '',
              '',
            ],
            remarks: currentPlan.h2_section.remarks || '',
          });
        }
      }

      setImportModalVisible(false);
      setImportJson('');
      setImportError('');
      message.success('執筆プランをインポートしました');
    } catch (error) {
      if (error instanceof SyntaxError) {
        setImportError(`JSON解析エラー: ${error.message}`);
      } else {
        console.error('[Step4.1] Import failed:', error);
        setImportError('インポートに失敗しました');
      }
    } finally {
      setSaving(false);
    }
  };

  // 執筆プランを確定して次のステップへ
  const handleConfirm = async () => {
    // 🔥 クレジット不足チェック
    if (usageCredits > availableCredits) {
      Modal.warning({
        title: 'クレジットが不足しています',
        content: (
          <div>
            <p>
              この生成には<strong> {usageCredits} クレジット</strong>
              が必要ですが、現在
              <strong> {availableCredits} クレジット</strong>しかありません。
            </p>
            <p>プランをアップグレードしてクレジットを追加してください。</p>
          </div>
        ),
        okText: 'プラン管理へ',
        onOk: () => {
          navigate('/subscription-management');
        },
      });
      return;
    }

    // 🔧 未保存の変更がある場合は先に自動保存
    if (hasUnsavedChanges && selectedSection && writingPlansData) {
      const saveSuccess = await handleSaveWritingPlan();
      if (!saveSuccess) {
        // 保存失敗時は確認ダイアログを表示
        Modal.confirm({
          title: '保存に失敗しました',
          content: '変更内容を保存できませんでした。保存せずに次のステップに進みますか？',
          okText: 'そのまま進む',
          cancelText: 'キャンセル',
          onOk: () => {
            proceedToNextStep();
          },
        });
        return;
      }
    }

    proceedToNextStep();
  };

  // 次のステップへ進む処理
  const proceedToNextStep = async () => {
    if (writingPlansData) {
      // 既に生成済みの場合は次のステップへ
      if (onCompleted) {
        onCompleted({ sections, writingPlansData });
      }
    } else {
      // 未生成の場合は生成開始
      await startGeneration();
    }
  };

  // 統計情報を計算（STEP2と同じロジック）
  const stats = useMemo(() => {
    // sectionsではなくworkingDraftSectionsから正確にカウント
    let h2Count = 0;
    let h3Count = 0;
    let h4Count = 0;

    workingDraftSections?.forEach(section => {
      // トップレベルはすべてH2扱い
      h2Count++;

      // subsectionsの中を走査
      if (section.subsections && Array.isArray(section.subsections)) {
        section.subsections.forEach(sub => {
          if (sub.type === 'heading' || sub.type === 'topping') {
            // type='heading'は通常H3、type='topping'は独自情報H3
            h3Count++;
          } else if (sub.type === 'h4') {
            h4Count++;
          }
        });
      }
    });

    // スタイルデータから文字数を計算
    const styleData = articleData?.style?.style_config;

    let articleLeadMin = 200;
    let articleLeadMax = 200;
    let h2BodyMin = 150;
    let h2BodyMax = 200;
    let h3BodyMin = 300;
    let h3BodyMax = 400;

    if (styleData?.headings_style?.length_policy) {
      const policy = styleData.headings_style.length_policy;

      // 記事リードの文字数レンジ
      const articleLeadStr = policy.article_lead_chars?.typical || '';
      const articleLeadMatch = articleLeadStr.match(/(\d+)-(\d+)/);
      if (articleLeadMatch) {
        articleLeadMin = parseInt(articleLeadMatch[1]);
        articleLeadMax = parseInt(articleLeadMatch[2]);
      } else if (articleLeadStr) {
        const parsed = parseInt(articleLeadStr);
        if (!isNaN(parsed)) {
          articleLeadMin = articleLeadMax = parsed;
        }
      }

      // H2直下の文字数レンジ
      const h2BodyStr = policy.h2_body_chars?.typical || '';
      const h2Match = h2BodyStr.match(/(\d+)-(\d+)/);
      if (h2Match) {
        h2BodyMin = parseInt(h2Match[1]);
        h2BodyMax = parseInt(h2Match[2]);
      } else if (h2BodyStr) {
        const parsed = parseInt(h2BodyStr);
        if (!isNaN(parsed)) {
          h2BodyMin = h2BodyMax = parsed;
        }
      }

      // H3本文の文字数レンジ
      const h3BodyStr = policy.h3_body_chars?.typical || '';
      const h3Match = h3BodyStr.match(/(\d+)-(\d+)/);
      if (h3Match) {
        h3BodyMin = parseInt(h3Match[1]);
        h3BodyMax = parseInt(h3Match[2]);
      } else if (h3BodyStr) {
        const parsed = parseInt(h3BodyStr);
        if (!isNaN(parsed)) {
          h3BodyMin = h3BodyMax = parsed;
        }
      }
    }

    // 独自情報H2の展開分のH3を数える
    let expansionH3Count = 0;
    workingDraftSections?.forEach(section => {
      if (section.isExpansionSection && section.autoExpansion?.count) {
        expansionH3Count += section.autoExpansion.count;
      }
    });
    const totalH3Count = h3Count + expansionH3Count;

    // 最小・最大を計算
    const minChars =
      articleLeadMin + h2Count * h2BodyMin + totalH3Count * h3BodyMin;
    const maxChars =
      articleLeadMax + h2Count * h2BodyMax + totalH3Count * h3BodyMax;

    // 100の倍数に丸める
    const estimatedMinChars = Math.round(minChars / 100) * 100;
    const estimatedMaxChars = Math.round(maxChars / 100) * 100;

    return {
      h2Count,
      h3Count,
      h4Count,
      estimatedMinChars,
      estimatedMaxChars,
      totalH3Count,
    };
  }, [articleData, workingDraftSections]);

  // 見出しタグ取得（独自情報対応）
  const getHeadingTag = (type, placementLevel) => {
    // toppingの場合、placementLevelに応じて表示
    if (type === 'topping') {
      // _child系（直上の見出し内に属する）はラベルなし（後でテキストで「└」表示）
      if (
        placementLevel === 'h2_child' ||
        placementLevel === 'h3_child' ||
        placementLevel === 'h4_child'
      ) {
        return { color: 'magenta', label: null, isChild: true };
      }
      // h2, h3, h4（セクションそのもの）はそれぞれのラベルでマゼンタ
      if (placementLevel === 'h2') {
        return { color: 'magenta', label: 'H2', isChild: false };
      } else if (placementLevel === 'h3') {
        return { color: 'magenta', label: 'H3', isChild: false };
      } else if (placementLevel === 'h4') {
        return { color: 'magenta', label: 'H4', isChild: false };
      }
      // デフォルト
      return { color: 'magenta', label: '独自', isChild: false };
    }

    const config = {
      h1: { color: 'red', label: 'H1', isChild: false },
      h2: { color: 'orange', label: 'H2', isChild: false },
      h3: { color: 'yellow', label: 'H3', isChild: false },
      h4: { color: 'green', label: 'H4', isChild: false },
    };
    return config[type] || { color: '#999', label: type, isChild: false };
  };

  // インデントレベル取得（独自情報対応）
  const getIndentLevel = (type, placementLevel) => {
    if (type === 'topping') {
      // _child系は親見出しのインデント + 追加インデント（12px）
      if (placementLevel === 'h2_child') {
        return 0 + 12;
      } else if (placementLevel === 'h3_child') {
        return 24 + 12;
      } else if (placementLevel === 'h4_child') {
        return 36 + 12;
      }
      // h2, h3, h4（セクションそのもの）は通常のインデント
      else if (placementLevel === 'h2') {
        return 0;
      } else if (placementLevel === 'h3') {
        return 24;
      } else if (placementLevel === 'h4') {
        return 36;
      } else {
        return 24; // デフォルト
      }
    }

    switch (type) {
      case 'h2':
        return 0;
      case 'h3':
        return 24;
      case 'h4':
        return 36;
      default:
        return 0;
    }
  };

  // Tourステップ定義（micro/fullを切り替え）
  const tourSteps = useMemo(() => {
    const configKey = isMicroMode ? 'generation_step4_1_micro' : 'generation_step4_1';
    if (tourLoading || !TOUR_CONFIGS[configKey]) return [];

    const allSteps = TOUR_CONFIGS[configKey].steps.map(step => {
      // tourConfigsで定義されたtarget関数をそのまま使う
      return {
        ...step,
        // target関数が既に定義されている場合はそのまま、なければnull
        target: step.target || null,
      };
    });

    return allSteps;
  }, [tourLoading, tourOpen]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 20px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 24, fontSize: 16, color: '#666' }}>
          構成案を読み込んでいます...
        </div>
      </div>
    );
  }

  return (
    <div className="writing-plan-layout">
      {/* チュートリアルTour */}
      {!tourLoading && (
        <EnhancedTour
          open={tourOpen}
          steps={tourSteps}
          onClose={() => completeTour()}
          onFinish={() => completeTour()}
          onSkip={() => skipTour()}
          current={currentStep}
          onChange={updateCurrentStep}
          tourTitle="STEP4.1: 執筆プラン"
        />
      )}

      {/* H1表示（全幅・STEPSの下） */}
      {h1Title && (
        <div
          className="tour-h1-title"
          style={{
            marginBottom: 32,
            paddingBottom: 20,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Tag
              color="red"
              style={{
                margin: 0,
                borderRadius: 999,
                fontSize: 12,
                padding: '2px 8px',
                lineHeight: '22px',
              }}
            >
              H1
            </Tag>
            <Text strong style={{ fontSize: 24, lineHeight: 1.4 }}>
              {h1Title}
            </Text>
          </div>
        </div>
      )}

      <Alert
        type="info"
        showIcon
        message={<span style={{ fontSize: 13 }}>記事の各セクションで具体的に何を書くか、アウトライン（執筆方針）を決めるフェーズです。左のセクションを選択し、右側のフォームで目標や書くべきポイントを確認・編集してください。そのまま進めることもできます。</span>}
        style={{ marginBottom: 16 }}
        closable
      />

      <Row gutter={24} className="step4-1-layout">
        {/* 左カラム: 見出し一覧 (60%) */}
        <Col span={15}>
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Button
                size="small"
                onClick={handleSaveWritingPlan}
                disabled={!selectedSection || !writingPlansData || saving}
                loading={saving}
              >
                一時保存する
              </Button>
              {/* 管理者・カスタムプランユーザー用：コピー/インポート機能 */}
              {(userRole === 'admin' || userRole === 'master' || userPlanType === 'custom') &&
                writingPlansData && (
                  <>
                    <Tooltip title="執筆プランをJSONでコピー">
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={handleCopyAsJson}
                      />
                    </Tooltip>
                    <Tooltip title="JSONから執筆プランをインポート">
                      <Button
                        size="small"
                        icon={<ImportOutlined />}
                        onClick={handleOpenImportModal}
                      />
                    </Tooltip>
                  </>
                )}
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                H2: {stats.h2Count}個
              </Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                H3: {stats.h3Count}個
              </Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                想定文字数: {stats.estimatedMinChars.toLocaleString()}~
                {stats.estimatedMaxChars.toLocaleString()}字
              </Text>
            </div>
          </div>

          <div className="outline-list-step4">
            {sections.map((section, index) => {
              const tagConfig = getHeadingTag(
                section.type,
                section.placementLevel,
              );
              const isSelected = selectedSection?.id === section.id;
              const indent = getIndentLevel(
                section.type,
                section.placementLevel,
              );

              return (
                <div
                  key={section.id || index}
                  className={`outline-item-step4 ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleSelectSection(section)}
                  style={{
                    padding:
                      section.children?.length > 0
                        ? `10px 12px 8px ${12 + indent}px`
                        : `10px 12px 10px ${12 + indent}px`,
                    cursor: 'pointer',
                    background: isSelected ? '#f5f5f5' : 'transparent',
                    transition: 'all 0.2s',
                  }}
                >
                  {/* 親見出し */}
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    {/* ラベル表示 */}
                    {tagConfig.label && (
                      <Tag
                        color={tagConfig.color}
                        style={{
                          margin: 0,
                          borderRadius: 999,
                          fontSize: 11,
                          padding: '0 6px',
                          lineHeight: '20px',
                        }}
                      >
                        {tagConfig.label}
                      </Tag>
                    )}
                    <Text style={{ flex: 1 }}>{section.text}</Text>

                    {/* 独自情報の場合、配置モードラベルを表示 */}
                    {section.type === 'topping' && (
                      <>
                        {section.placementMode === 'inline_part' && (
                          <Tag
                            color="purple"
                            style={{
                              margin: 0,
                              fontSize: 11,
                              padding: '0 6px',
                              lineHeight: '20px',
                            }}
                          >
                            パーツ
                          </Tag>
                        )}
                        {section.placementMode === 'section_embed' && (
                          <Tag
                            color="cyan"
                            style={{
                              margin: 0,
                              fontSize: 11,
                              padding: '0 6px',
                              lineHeight: '20px',
                            }}
                          >
                            適宜
                          </Tag>
                        )}
                        {section.placementLevel &&
                          section.placementLevel.includes('h2') &&
                          !section.placementLevel.includes('child') && (
                            <Tag
                              color="geekblue"
                              style={{
                                margin: 0,
                                fontSize: 11,
                                padding: '0 6px',
                                lineHeight: '20px',
                              }}
                            >
                              展開
                            </Tag>
                          )}
                        <DatabaseOutlined
                          style={{ color: '#eb2f96', fontSize: 14 }}
                        />
                      </>
                    )}
                  </div>

                  {/* 子要素（└アイテム）- 親アイテムの中に表示（クリッカブル不要） */}
                  {section.children && section.children.length > 0 && (
                    <div
                      style={{
                        marginTop: 8,
                        paddingLeft: 24,
                        borderLeft: '2px solid #fafafa',
                      }}
                    >
                      {section.children.map((child, childIndex) => {
                        // 子要素が独自情報かH4かで色を変える
                        const isTopping = child.type === 'topping';
                        const childColor = isTopping ? '#eb2f96' : 'inherit'; // 独自情報はピンク、H4は通常色

                        return (
                          <div
                            key={child.id || `child-${childIndex}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '6px 0',
                            }}
                          >
                            <Text
                              style={{
                                color: childColor,
                                fontSize: 13,
                                fontWeight: isTopping ? 500 : 'normal',
                              }}
                            >
                              └
                            </Text>
                            {/* H4の場合はラベルを表示 */}
                            {child.type === 'h4' && (
                              <Tag
                                color="green"
                                style={{
                                  margin: 0,
                                  borderRadius: 999,
                                  fontSize: 10,
                                  padding: '0 5px',
                                  lineHeight: '18px',
                                }}
                              >
                                H4
                              </Tag>
                            )}
                            <Text
                              style={{
                                flex: 1,
                                color: childColor,
                                fontSize: 13,
                              }}
                            >
                              {child.text}
                            </Text>
                            {isTopping && (
                              <DatabaseOutlined
                                style={{ color: childColor, fontSize: 13 }}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Col>

        {/* 右カラム: 選択したセクションの詳細 (40%) */}
        <Col span={9}>
          {generating && !writingPlansData ? (
            // 生成中はスケルトン表示
            <div style={{ paddingTop: 36 }}>
              <Skeleton active paragraph={{ rows: 2 }} />
              <div style={{ marginTop: 24 }}>
                <Skeleton.Input
                  active
                  size="large"
                  block
                  style={{ height: 120 }}
                />
              </div>
              <div style={{ marginTop: 20 }}>
                <Skeleton.Input
                  active
                  size="large"
                  block
                  style={{ height: 80 }}
                />
              </div>
              <div style={{ marginTop: 20 }}>
                <Skeleton.Input
                  active
                  size="large"
                  block
                  style={{ height: 80 }}
                />
              </div>
            </div>
          ) : selectedSection ? (
            <div className="section-detail-form" style={{ paddingTop: 36 }}>
              {/* 選択した見出し表示 */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  marginBottom: 24,
                }}
              >
                {/* ラベル表示（_childの場合はラベルなし） */}
                {getHeadingTag(
                  selectedSection.type,
                  selectedSection.placementLevel,
                ).label && (
                  <Tag
                    color={
                      getHeadingTag(
                        selectedSection.type,
                        selectedSection.placementLevel,
                      ).color
                    }
                    style={{
                      margin: 0,
                      borderRadius: 999,
                      fontSize: 11,
                      padding: '0 6px',
                      lineHeight: '20px',
                      flexShrink: 0,
                    }}
                  >
                    {
                      getHeadingTag(
                        selectedSection.type,
                        selectedSection.placementLevel,
                      ).label
                    }
                  </Tag>
                )}
                {/* _childの場合は「└」テキストを表示 */}
                {getHeadingTag(
                  selectedSection.type,
                  selectedSection.placementLevel,
                ).isChild && (
                  <Text
                    style={{
                      color: '#eb2f96',
                      fontSize: 14,
                      fontWeight: 500,
                      flexShrink: 0,
                    }}
                  >
                    └
                  </Text>
                )}
                <Text
                  style={{
                    flex: 1,
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                    fontWeight: 500,
                    color: getHeadingTag(
                      selectedSection.type,
                      selectedSection.placementLevel,
                    ).isChild
                      ? '#eb2f96'
                      : 'inherit',
                  }}
                >
                  {selectedSection.text}
                </Text>
                {/* 独自情報の場合は右側にマーク */}
                {selectedSection.type === 'topping' && (
                  <DatabaseOutlined
                    style={{ color: '#eb2f96', fontSize: 14 }}
                  />
                )}
              </div>

              {/* H4リストを表示（選択されているセクションがH3で、childrenにH4がある場合） */}
              {selectedSection.type === 'h3' &&
                selectedSection.children &&
                selectedSection.children.some(child => child.type === 'h4') && (
                  <div style={{ marginBottom: 16 }}>
                    {selectedSection.children
                      .filter(child => child.type === 'h4')
                      .map((h4, index) => (
                        <div
                          key={h4.id || index}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            marginBottom: 4,
                          }}
                        >
                          <Text style={{ fontSize: 13, color: '#999' }}>└</Text>
                          <Tag
                            color="green"
                            style={{
                              margin: 0,
                              fontSize: 10,
                              padding: '0 5px',
                              lineHeight: '16px',
                            }}
                          >
                            H4
                          </Tag>
                          <Text style={{ fontSize: 13 }}>{h4.text}</Text>
                        </div>
                      ))}
                  </div>
                )}

              {/* 独自情報（パーツモード）の場合はコンテンツを表示 */}
              {selectedSection.type === 'topping' &&
              selectedSection.placementMode === 'inline_part' ? (
                <div style={{ marginBottom: 20 }}>
                  <Text
                    strong
                    style={{
                      display: 'block',
                      marginBottom: 8,
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#939393',
                    }}
                  >
                    パーツの内容
                  </Text>
                  <div
                    style={{
                      padding: 12,
                      background: '#fafafa',
                      borderRadius: 4,
                      maxHeight: 300,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'monospace',
                      fontSize: 12,
                    }}
                  >
                    {selectedSection.content || '（コンテンツがありません）'}
                  </div>
                </div>
              ) : (
                <>
                  {/* セクションのゴール */}
                  <div style={{ marginBottom: 20 }}>
                    <Text
                      strong
                      style={{
                        display: 'block',
                        marginBottom: 8,
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#939393',
                      }}
                    >
                      セクションのゴール
                    </Text>
                    <TextArea
                      value={formData.section_goal}
                      onChange={e =>
                        handleFormChange('section_goal', e.target.value)
                      }
                      rows={5}
                      placeholder="このセクションで読者に何を理解させるか"
                      className="custom-textarea"
                    />
                  </div>

                  {/* アウトライン */}
                  <div style={{ marginBottom: 20 }}>
                    <Text
                      strong
                      style={{
                        display: 'block',
                        marginBottom: 8,
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#939393',
                      }}
                    >
                      アウトライン
                    </Text>
                    {formData.outline_points.map((point, idx) => (
                      <div key={idx} style={{ marginBottom: 8 }}>
                        <TextArea
                          value={point}
                          onChange={e =>
                            handleFormChange(
                              'outline_points',
                              e.target.value,
                              idx,
                            )
                          }
                          rows={2}
                          placeholder={`アウトライン項目 ${idx + 1}`}
                          className="custom-textarea"
                        />
                      </div>
                    ))}
                  </div>

                  {/* 補足 */}
                  <div>
                    <Text
                      strong
                      style={{
                        display: 'block',
                        marginBottom: 8,
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#939393',
                      }}
                    >
                      補足
                    </Text>
                    <TextArea
                      value={formData.remarks}
                      onChange={e =>
                        handleFormChange('remarks', e.target.value)
                      }
                      rows={4}
                      placeholder="執筆時の注意点や追加の指示"
                      className="custom-textarea"
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#999',
              }}
            >
              左側から見出しを選択してください
            </div>
          )}
        </Col>
      </Row>

      {/* アクションボタン（全幅） */}
      <div style={{ marginTop: 24 }}>
        {/* 🔥 クレジット情報ボックス */}
        {!loadingCredits && usageCredits > 0 && (
          <div
            style={{
              background: '#f5f5f5',
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              padding: '12px 16px',
              marginBottom: 12,
              fontSize: 13,
              lineHeight: '1.6',
              textAlign: 'center',
            }}
          >
            <div style={{ marginBottom: 4 }}>
              <FireOutlined style={{ color: '#ff7a45', marginRight: 6 }} />
              <Text strong>
                従量クレジットが {usageCredits} クレジットで確定・消費されます
              </Text>
            </div>
            <div style={{ color: '#666', fontSize: 12 }}>
              ※ 本文生成をやり直す場合は、追加でクレジットが消費されます
            </div>
          </div>
        )}

        <Button
          type="primary"
          size="large"
          icon={<CheckOutlined />}
          onClick={handleConfirm}
          loading={generating}
          disabled={isReadOnly || generating || loadingCredits}
          block
        >
          <span>
            {generating
              ? '執筆プランを生成中...'
              : '執筆プランを確定して本文を生成する'}
          </span>
          {!generating && !loadingCredits && usageCredits > 0 && (
            <span
              style={{
                marginLeft: 8,
              }}
            >
              <FireOutlined style={{ marginRight: 4 }} /> {usageCredits}
            </span>
          )}
        </Button>
      </div>

      {/* 管理者用：JSONインポートモーダル */}
      <Modal
        title="執筆プランをJSONからインポート"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setImportJson('');
          setImportError('');
        }}
        onOk={handleImportJson}
        okText="インポート"
        cancelText="キャンセル"
        confirmLoading={saving}
        width={700}
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            コピーした執筆プランのJSONを貼り付けてください。
            既存の見出しは上書きされ、新しい見出しは追加されます。
          </Text>
        </div>
        {importError && (
          <Alert
            type="error"
            message={importError}
            style={{ marginBottom: 12 }}
            showIcon
          />
        )}
        <TextArea
          rows={15}
          value={importJson}
          onChange={e => {
            setImportJson(e.target.value);
            setImportError('');
          }}
          placeholder='{"plans": [...]}'
          style={{ fontFamily: 'monospace', fontSize: 12 }}
        />
      </Modal>
    </div>
  );
};

export default Step4_1_WritingPlan;
